import { z } from 'zod';
import { isFieldErrorTimingOpen } from '../components/Form/fieldErrorVisibility';
import { PAYMENT_TERMS_REGEX } from './organisation.schema';
import type { Salesperson } from '@api/orders';

// Validation message keys (match i18n keys in order_details.edit_order.validation).
// Passed in from the component so copy stays in en.yml; the static defaults below
// are fallbacks only (tests / non-i18n callers).
//
// Presence rules only — deliberately NO `.refine`/`.superRefine` (the shared
// @components/Form runs the legacy zod adapter that dead-locks on refine; see
// contact.schema.ts). Phone *format* is checked imperatively via
// isValidContactMobile in the edit form, mirroring Contacts.
export type UpdateOrderDetailsValidationMessages = {
  contactRequired?: string;
  phoneRequired?: string;
  sourceRequired?: string;
  salespersonRequired?: string;
  paymentTermsInvalid?: string;
};

const DEFAULT_VALIDATION_MESSAGES: Required<UpdateOrderDetailsValidationMessages> = {
  contactRequired: 'Contact is required',
  phoneRequired: 'Contact number is required',
  sourceRequired: 'Source is required',
  salespersonRequired: 'Salesperson is required',
  paymentTermsInvalid: 'Enter a whole number of days',
};

// hasSalesperson: once an order already has a salesperson, order.rb rejects
// clearing it (salesperson_cannot_be_cleared) -- surface that inline instead
// of round-tripping to a 422. Orders that don't have one yet keep the field
// optional. Conditional type selection, not `.refine`, per the file-level note above.
export function makeUpdateOrderDetailsSchema(
  messages?: UpdateOrderDetailsValidationMessages,
  hasSalesperson = false,
) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };

  return z.object({
    // Submitted to API (order-level)
    contact_id: z.number().min(1, m.contactRequired),
    organisation_id: z.number().nullable().optional(),
    salesperson_id: hasSalesperson
      ? z.number({ error: m.salespersonRequired })
      : z.number().nullable().optional(),
    // Required on the edit form (GRAIN-2588). Safe: order.rb assign_default_order_source
    // gives every order a source, so an existing order is never sourceless and edit is
    // never blocked. Frontend-form only — backend keeps order_source optional.
    order_source_id: z.string().min(1, m.sourceRequired),
    is_outbound: z.boolean().default(true),
    // Contact-level fields persisted via the contact update endpoint
    contact_email: z.string(),
    contact_name: z.string(),
    // Presence here; libphonenumber format enforced imperatively in the form.
    contact_phone: z.string().min(1, m.phoneRequired),
    customer_notes: z.string().optional().default(''),
    // The billed party travels as the picked row's id; the order derives its
    // snapshot from that row server-side.
    selected_billing_address_id: z.number().nullable().optional().default(null),
    // Editable copy of the picked row's street, postal code, and name, sent
    // alongside the id: the backend writes them to that row and re-derives the
    // order's snapshot in one transaction. Format is not checked here — rows
    // imported from Papercut carry legacy nonconforming codes, and an untouched
    // one must not block a save.
    billing_address: z.string().optional().default(''),
    billing_postal_code: z.string().optional().default(''),
    // Free-text label on the picked row (GRAIN-2964). Blank is valid — the
    // order then bills to the customer's name.
    billing_name: z.string().optional().default(''),
    // The linked organisation's net payment days. Saved to the organisation,
    // not the order — nothing here reaches the order payload.
    // Held as a string, blank = "no terms"; the digit cap rejects absurd input
    // immediately, while Organisation::MAX_PAYMENT_TERMS_IN_DAYS is enforced
    // server-side and comes back as an inline field error.
    payment_terms_in_days: z.string().regex(PAYMENT_TERMS_REGEX, m.paymentTermsInvalid).optional().default(''),
    // What the selected organisation currently holds, re-read whenever that
    // organisation changes. Never edited — it exists so submit can tell an
    // actual edit from a re-seed and skip a no-op write to the organisation.
    organisation_payment_terms_in_days: z.string().optional().default(''),
    // Display-only
    organisation_name: z.string().optional(),
  });
}

// Static schema with default messages (for tests / non-i18n callers).
export const updateOrderDetailsSchema = makeUpdateOrderDetailsSchema();

export type UpdateOrderDetailsFormData = z.infer<typeof updateOrderDetailsSchema>;

// Create order (GRAIN-2588). §1: the Create CTA is always enabled — these
// presence rules surface inline on submit-click, they never gate the button.
// Presence only, no `.refine` (shared @components/Form adapter dead-locks on it).
// contact_id / order_source_id / salesperson_id are the create required set,
// mirroring the pre-seam `isValid` (a customer, a source, and a salesperson).
// The remaining fields carry the contact write-back values through submit.
export type CreateOrderValidationMessages = {
  contactRequired?: string;
  sourceRequired?: string;
  salespersonRequired?: string;
};

const DEFAULT_CREATE_ORDER_MESSAGES: Required<CreateOrderValidationMessages> = {
  contactRequired: 'Please select a customer',
  sourceRequired: 'Source is required',
  salespersonRequired: 'Salesperson is required',
};

export function makeCreateOrderSchema(messages?: CreateOrderValidationMessages) {
  const m = { ...DEFAULT_CREATE_ORDER_MESSAGES, ...messages };

  return z.object({
    contact_id: z.number().min(1, m.contactRequired),
    contact_email: z.string().optional().default(''),
    contact_name: z.string().optional().default(''),
    contact_phone: z.string().optional().default(''),
    customer_notes: z.string().optional().default(''),
    organisation_id: z.number().nullable().optional().default(null),
    organisation_name: z.string().optional().default(''),
    order_source_id: z.string().min(1, m.sourceRequired),
    is_outbound: z.boolean().default(false),
    salesperson_id: z.number().min(1, m.salespersonRequired),
  });
}

export const createOrderSchema = makeCreateOrderSchema();

export type CreateOrderFormData = z.infer<typeof createOrderSchema>;

// The picker only lists eligible sales-team members, so an ineligible value is
// never something the user can act on — a stale default (defaultSalespersonId
// falling back to the current admin) is the only way to get one. The field just
// turns red; order.rb's salesperson_must_be_sales_team is the server-side backstop.
export function isSalespersonFieldInvalid(
  meta: { errors?: unknown[]; isBlurred?: boolean; isDirty?: boolean },
  value: number | null | undefined,
  submitted: boolean,
  salespersons: Salesperson[],
): boolean {
  if (!isFieldErrorTimingOpen(meta, submitted)) return false;
  return Boolean(meta.errors?.length) || Boolean(value && !salespersons.some((sp) => sp.id === value));
}
